import * as React from "react";

import { cn, getInitialsFromDisplayName } from "@/utils/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { User, useOrganizationUsersQuery } from "@common/utils/graphql";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useState } from "react";
import { Check, Plus, X } from "@phosphor-icons/react";

interface Props {
  placeholder: string;
  onChange: (emails: User[]) => void;
}

export const MultiEmployeeSelect = ({ onChange, placeholder }: Props) => {
  const [searchText, setSearchText] = useState("");

  const { data, loading } = useOrganizationUsersQuery({
    variables: {
      offset: 0,
      pageSize: 3,
      name: searchText || null,
    },
  });

  const [selected, setSelected] = React.useState<User[]>([]);

  const [open, setOpen] = React.useState(false);
  const changeList = (newList: User[]) => {
    onChange(newList);
    setSelected(newList);
  };

  const employees = React.useMemo(() => {
    if (
      loading ||
      data?.organizationUsers.__typename !== "QueryOrganizationUsersSuccess"
    ) {
      return [];
    }
    return data.organizationUsers.data.users;
  }, [data, loading]);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between border-none bg-[#1c1c1c] text-[#a1a1aa]"
          >
            {placeholder}
            <Plus className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[335px] p-0">
          <Command>
            <CommandInput
              onValueChange={setSearchText}
              value={searchText}
              placeholder="Search employees"
            />
            <CommandEmpty>
              <div>
                {loading ? (
                  <p>Searching...</p>
                ) : 0 < searchText.length && searchText.length < 3 ? (
                  <p>Type in at least three characters to search</p>
                ) : (
                  <div>
                    <p>No employees found</p>
                  </div>
                )}
              </div>
            </CommandEmpty>
            <CommandGroup>
              {searchText.length < 3 && employees.length > 0 && (
                <p className="p-3 text-center text-sm">Recommended users</p>
              )}
              {searchText.length >= 0 &&
                employees.map((user) => (
                  <CommandItem
                    key={user.employee?.workEmail}
                    value={user.employee?.workEmail}
                    onSelect={(email) => {
                      const alreadyExists = selected.find(
                        (user) => user.employee?.workEmail === email,
                      );
                      if (alreadyExists) {
                        changeList(
                          selected.filter(
                            (user) => user?.employee?.workEmail !== email,
                          ),
                        );
                      } else {
                        changeList([...selected, user as User]);
                      }
                      setOpen(false);
                    }}
                  >
                    <div className="flex w-full justify-between">
                      <div className="flex flex-row">
                        <Avatar>
                          <AvatarFallback>
                            {getInitialsFromDisplayName(user.displayName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col pl-2">
                          <div>{user.displayName}</div>
                          <div>{user.employee?.workEmail}</div>
                        </div>
                      </div>
                      <div className="flex-end flex items-center justify-end">
                        <Check
                          className={cn(
                            "w-100 mr-2 size-4",
                            selected.find(
                              (e) =>
                                e.employee?.workEmail ===
                                user.employee?.workEmail,
                            )
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                      </div>
                    </div>
                  </CommandItem>
                ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
      <div>
        {selected.map((user) => {
          return (
            <div className="flex w-full justify-between pt-4" key={user.userId}>
              <div className="flex flex-row items-center">
                <Avatar>
                  <AvatarFallback>
                    {getInitialsFromDisplayName(user.displayName)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col pl-2">
                  <div>
                    {user.firstName} {user.lastName}
                  </div>
                  <p className="text-slate">{user.employee?.workEmail}</p>
                </div>
              </div>
              <div className="flex-end flex items-center justify-end">
                <X
                  onClick={() =>
                    changeList(
                      selected.filter(
                        (employee) =>
                          employee.employee?.workEmail !==
                          user.employee?.workEmail,
                      ),
                    )
                  }
                  className={cn("w-100 mr-2 size-4 cursor-pointer opacity-100")}
                />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};
